import React, { useState } from 'react';
import {
  Container, Card, Table, Form, Badge, Alert, Spinner, Button, Tabs, Tab,
} from 'react-bootstrap';
import {
  useGetBloomUsersQuery,
  usePinBloomUserMutation,
} from '../../app/api';

// Tech -> Bloom Accounts.
//
// Bloom Growth's API exposes no email addresses — its user records carry only
// an id, a name and an avatar — so employees are matched to Bloom by full name.
// This page shows where that works and where it doesn't, and lets Tech fix the
// two failure modes by pinning a person to a specific Bloom id.

export default function BloomUsers() {
  const [refreshing, setRefreshing] = useState(false);
  const { data, isLoading, isError, error, refetch } = useGetBloomUsersQuery({});
  const [pin] = usePinBloomUserMutation();

  const [busyId, setBusyId] = useState(null);
  const [saveError, setSaveError] = useState('');
  const [query, setQuery] = useState('');

  const counts = data?.counts || {};
  const unmatched = data?.unmatched || [];
  const matched = data?.matched || [];
  const ambiguous = data?.ambiguous || [];
  const bloomOnly = data?.bloom_only || [];

  const filt = (rows) => {
    const q = query.trim().toLowerCase();
    if (!q) return rows;
    return rows.filter(r => (r.name || '').toLowerCase().includes(q));
  };

  const doPin = async (row, bloomUserId) => {
    setBusyId(row.directory_id);
    setSaveError('');
    try {
      await pin({ directory_id: row.directory_id, bloom_user_id: bloomUserId }).unwrap();
      await refetch();
    } catch (e) {
      setSaveError(e?.data?.detail || 'Could not save.');
    } finally {
      setBusyId(null);
    }
  };

  const hardRefresh = async () => {
    setRefreshing(true);
    try {
      // Bypasses the six-hour cache and re-reads every meeting from Bloom.
      await refetch();
    } finally {
      setRefreshing(false);
    }
  };

  if (isLoading) {
    return (
      <Container className="py-4 text-center">
        <Spinner animation="border" />
        <p className="text-muted mt-2" style={{ fontSize: '0.85rem' }}>
          Reading Bloom…
        </p>
      </Container>
    );
  }

  if (isError) {
    return (
      <Container className="py-4">
        <Alert variant="danger">
          {error?.data?.detail || 'Could not load Bloom accounts.'}
        </Alert>
      </Container>
    );
  }

  if (data && !data.configured) {
    return (
      <Container className="py-4">
        <h3>Bloom Accounts</h3>
        <Alert variant="secondary">
          No Bloom service account is configured, so nothing can be matched.
          Set <code>BLOOM_USERNAME</code> and <code>BLOOM_PASSWORD</code> first.
        </Alert>
      </Container>
    );
  }

  // A pin control shared by the ambiguous and unmatched tabs. Ambiguous rows
  // already know their candidates, so those become buttons; unmatched rows take
  // a free-typed id, since there is no candidate to offer.
  const pinControl = (row) => {
    const busy = busyId === row.directory_id;
    if (busy) return <Spinner animation="border" size="sm" />;
    if (row.candidates?.length) {
      return (
        <div className="d-flex gap-1 flex-wrap">
          {row.candidates.map(c => (
            <Button key={c.id} size="sm" variant="outline-primary"
                    onClick={() => doPin(row, String(c.id))}>
              Use {c.id}
            </Button>
          ))}
        </div>
      );
    }
    return (
      <Form
        className="d-flex gap-1"
        onSubmit={(e) => {
          e.preventDefault();
          const v = e.target.elements.bid.value.trim();
          if (v) doPin(row, v);
        }}
      >
        <Form.Control name="bid" size="sm" placeholder="Bloom id" style={{ maxWidth: '8rem' }} />
        <Button size="sm" type="submit" variant="outline-secondary">Pin</Button>
      </Form>
    );
  };

  const peopleTable = (rows, showPin) => (
    <Card>
      <Table responsive hover className="mb-0 align-middle">
        <thead>
          <tr>
            <th style={{ width: '26%' }}>Employee</th>
            <th style={{ width: '30%' }}>Title</th>
            <th style={{ width: '18%' }}>Bloom</th>
            {showPin && <th>Pin to a Bloom account</th>}
          </tr>
        </thead>
        <tbody>
          {rows.map(r => (
            <tr key={r.directory_id}>
              <td className="fw-semibold">{r.name}</td>
              <td className="text-muted" style={{ fontSize: '0.82rem' }}>{r.title || '—'}</td>
              <td style={{ fontSize: '0.82rem' }}>
                {r.bloom_user_id
                  ? <>
                      {r.bloom_user_id}{' '}
                      <Badge bg={r.how === 'override' ? 'primary' : 'success'}>
                        {r.how === 'override' ? 'pinned' : 'by name'}
                      </Badge>
                    </>
                  : <Badge bg={r.how === 'ambiguous' ? 'warning' : 'secondary'}
                           text={r.how === 'ambiguous' ? 'dark' : undefined}>
                      {r.how}
                    </Badge>}
              </td>
              {showPin && <td>{pinControl(r)}</td>}
            </tr>
          ))}
          {rows.length === 0 && (
            <tr><td colSpan={showPin ? 4 : 3} className="text-center text-muted py-4">
              Nobody here.
            </td></tr>
          )}
        </tbody>
      </Table>
    </Card>
  );

  return (
    <Container className="py-4">
      <div className="d-flex align-items-start gap-3 flex-wrap mb-1">
        <div style={{ flex: 1, minWidth: '20rem' }}>
          <h3 className="mb-1">Bloom Accounts</h3>
          <p className="text-muted" style={{ maxWidth: '46rem' }}>
            Bloom’s API doesn’t expose email addresses, so employees are matched
            to it by name. This is where that succeeds and where it needs help.
          </p>
        </div>
        <Button variant="outline-secondary" size="sm" onClick={hardRefresh} disabled={refreshing}>
          {refreshing ? <Spinner animation="border" size="sm" /> : <i className="bi bi-arrow-clockwise" />}
          {' '}Re-read Bloom
        </Button>
      </div>

      {saveError && <Alert variant="danger" dismissible onClose={() => setSaveError('')}>{saveError}</Alert>}

      <div className="d-flex gap-3 flex-wrap mb-3" style={{ fontSize: '0.85rem' }}>
        <span><strong>{counts.matched}</strong> matched</span>
        <span className="text-muted">·</span>
        <span><strong>{counts.unmatched}</strong> without a Bloom account</span>
        <span className="text-muted">·</span>
        <span><strong>{counts.ambiguous}</strong> ambiguous</span>
        <span className="text-muted">·</span>
        <span className="text-muted">
          {counts.bloom_users} Bloom user{counts.bloom_users === 1 ? '' : 's'} seen
        </span>
      </div>

      {ambiguous.length > 0 && (
        <Alert variant="warning" className="py-2" style={{ fontSize: '0.88rem' }}>
          <strong>{ambiguous.length} name{ambiguous.length === 1 ? '' : 's'} match more than one
          Bloom account.</strong> These deliberately match nobody — showing one person another’s
          rocks and to-dos is worse than showing nothing. Pick the right id below.
        </Alert>
      )}

      <Form.Control
        className="mb-3"
        style={{ maxWidth: '20rem' }}
        placeholder="Search by name…"
        value={query}
        onChange={e => setQuery(e.target.value)}
      />

      <Tabs defaultActiveKey="unmatched" className="mb-3">
        <Tab eventKey="unmatched" title={`No Bloom account (${unmatched.length})`}>
          <Alert variant="secondary" className="py-2" style={{ fontSize: '0.85rem' }}>
            Usually correct — these people aren’t on any L10 the service account can
            see. Add it to their meeting, or pin them if their Bloom name simply
            differs from the directory (e.g. “Mike” vs “Michael”).
          </Alert>
          {peopleTable(filt(unmatched), true)}
        </Tab>

        <Tab eventKey="ambiguous" title={`Ambiguous (${ambiguous.length})`}>
          {peopleTable(filt(ambiguous), true)}
        </Tab>

        <Tab eventKey="matched" title={`Matched (${matched.length})`}>
          {peopleTable(filt(matched), true)}
        </Tab>

        <Tab eventKey="bloomonly" title={`Bloom-only (${bloomOnly.length})`}>
          <Alert variant="secondary" className="py-2" style={{ fontSize: '0.85rem' }}>
            Bloom accounts no active employee claims — leavers, people outside the
            directory, or the unused half of a duplicate pair. Useful for deciding
            whether someone in the first tab is a real gap.
          </Alert>
          <Card>
            <Table responsive hover className="mb-0 align-middle">
              <thead><tr><th style={{ width: '30%' }}>Bloom user</th><th>Id</th></tr></thead>
              <tbody>
                {filt(bloomOnly).map(b => (
                  <tr key={b.bloom_user_id}>
                    <td className="fw-semibold">{b.name}</td>
                    <td className="text-muted" style={{ fontSize: '0.82rem' }}>{b.bloom_user_id}</td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </Card>
        </Tab>
      </Tabs>
    </Container>
  );
}
